'use client';

import { Popover, PopoverContent, PopoverTrigger, Sheet, SheetContent, SheetTrigger } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { getScrollBehavior, useIsMobile, useOffsetHeaders } from '@shared/utils';
import { useEffect, useState } from 'react';
import { BalanceBreakdown } from '../../BalanceBreakdown/BalanceBreakdown';
import { WalletButton } from '../WalletButton/WalletButton';

type WalletPopoverProps = {
  balance: string;
  zIndex?: string;
};

/**
 * Responsive wallet component that shows balance breakdown in a popover (desktop) or sheet (mobile).
 * Uses WalletButton as trigger and displays BalanceBreakdown content.
 */
function WalletPopover({ balance, zIndex = 'z-40' }: WalletPopoverProps) {
  const isMobile = useIsMobile({ breakpoint: 'md' });
  const { offsetPrimaryHeader } = useOffsetHeaders();
  const [isOpen, setIsOpen] = useState(false);
  const handlePopupClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isMobile && isOpen) {
      // Scroll to top when sheet opens on mobile to avoid misalignment
      window.scrollTo({ top: 0, behavior: getScrollBehavior() });
    }
  }, [isMobile, isOpen]);

  return isMobile ? (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <WalletButton balance={balance} isOpen={isOpen} />
      </SheetTrigger>
      <SheetContent
        className={twMerge('p-5 rounded-b-lg', zIndex)}
        side="top"
        customOverlayClassName={zIndex}
        customOverlayStyle={{
          top: offsetPrimaryHeader,
        }}
        style={{
          top: offsetPrimaryHeader,
        }}
        showCloseButton={false}
      >
        <BalanceBreakdown namespace="my_account.wallet" onActionClick={handlePopupClose} />
      </SheetContent>
    </Sheet>
  ) : (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <WalletButton balance={balance} isOpen={isOpen} />
      </PopoverTrigger>
      <PopoverContent sideOffset={12} className="w-91 border-none popover-shadow">
        <BalanceBreakdown namespace="my_account.wallet" onActionClick={handlePopupClose} />
      </PopoverContent>
    </Popover>
  );
}

export { WalletPopover };
